import { Box, Chip, FormHelperText, type SxProps, type Theme } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemTimePicker from "./SystemTimePicker";

/**
 * DST-free anchor for the picker: only the hour matters, and building a DateTime on a real transition
 * day can land on a local time that doesn't exist. January 1 is never a transition.
 */
const CLOCK_ANCHOR = { year: 2000, month: 1, day: 1 } as const;

/** Single-consumer geometry, not a token: the picker sits beside wrapped chips inside a settings row. */
const PICKER_WIDTH = 140;

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const hourLabel = (hour: number): string => DateTime.fromObject({ ...CLOCK_ANCHOR, hour }).toFormat("h:mm a");

/**
 * Edit a list of whole clock hours — the configured hours as removable chips, plus a `SystemTimePicker`
 * that appends the next one and clears itself.
 *
 * Sibling of `TimeRangeListField`, for the case where each entry is a single INSTANT rather than a
 * range. Because entries are instants they cannot overlap; the only degenerate input is a duplicate,
 * which is silently ignored (the chip the user is asking for is already on screen).
 *
 * Deliberately NOT free text. `TimeRangeListField`'s docstring records free entry as the DAT-728
 * failure mode — a typed, uncommitted value looked saved and wasn't — and that is worse here, where
 * the value feeds census arithmetic. A constrained picker also cannot produce an out-of-range hour.
 *
 * Sub-hour entries are rounded to the nearest hour on commit, with helper text saying so, rather than
 * rejected: rejecting leaves the user in a dead end with no way to tell what the control wants.
 */
export const HourListField: FC<{
	/** The configured hours, 0–23. Rendered in the order given — sort upstream if order matters. */
	value: number[];
	onChange: (next: number[]) => void;
	disabled?: boolean;
	/** Overrides the picker's label; defaults to a generic "Add time". */
	pickerLabel?: string;
	/** Forwarded to the picker — settings tables pass their dense inline-editor sx. */
	fieldSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	/** Stamped on the wrapper; the picker derives `-picker` and each chip `-chip-{hour}` from it. */
	dataTestId?: string;
}> = ({ value, onChange, disabled = false, pickerLabel, fieldSx, sx, dataTestId }) => {
	const { t } = useTranslation();
	const [pending, setPending] = useState<DateTime | null>(null);
	const [wasRounded, setWasRounded] = useState(false);
	const [pickerNonce, setPickerNonce] = useState(0);

	/**
	 * Commit on ACCEPT, never on change. MUI emits a perfectly valid DateTime as soon as the hour
	 * section is filled — with the meridiem still unset, so it reads as AM. Appending there consumed
	 * the entry and reset the field before the user could reach PM, making every PM time impossible
	 * to enter. `onAccept` fires only once the user is done (the action bar's OK, or the last section
	 * completing), which is the real "this is my answer" signal.
	 */
	const accept = useCallback(
		(next: DateTime | null) => {
			if (!next?.isValid) {
				setWasRounded(false);
				return;
			}

			// 23:30 rounds up to 24, which is midnight of the next day — the same reading as hour 0.
			const hour = Math.round(next.hour + next.minute / MINUTES_PER_HOUR) % HOURS_PER_DAY;
			setWasRounded(next.minute !== 0);
			setPending(null);
			// Blanking the field isn't enough: MUI remembers the value it last accepted and stays silent if
			// the user picks it again, so deleting an hour's chip and re-adding that hour would emit no
			// `onAccept` at all. Remounting the picker is what clears that memory.
			setPickerNonce((previous) => previous + 1);
			if (!value.includes(hour)) onChange([...value, hour]);
		},
		[value, onChange],
	);

	const removeHour = useCallback((hour: number) => onChange(value.filter((existing) => existing !== hour)), [value, onChange]);

	return (
		<Box data-testid={dataTestId} sx={sx}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
				{value.map((hour) => (
					<Chip
						key={hour}
						size="small"
						variant="outlined"
						label={hourLabel(hour)}
						onDelete={disabled ? undefined : () => removeHour(hour)}
						data-testid={dataTestId && `${dataTestId}-chip-${hour}`}
					/>
				))}
				<SystemTimePicker
					key={`picker-${pickerNonce}`}
					value={pending}
					// Holds the half-entered value in the field; the append happens in `accept`.
					onChange={setPending}
					onAccept={accept}
					label={pickerLabel ?? t("hour.list.add")}
					disabled={disabled}
					// The remount above drops focus; restore it for the next entry, but never on the first
					// mount, where a settings page full of these would fight over the cursor.
					autoFocus={pickerNonce > 0}
					// Whole hours only: the wheel offers nothing else, so rounding is a keyboard-entry fallback.
					minutesStep={MINUTES_PER_HOUR}
					fullWidth={false}
					sx={[{ width: PICKER_WIDTH, flexShrink: 0 }, ...(Array.isArray(fieldSx) ? fieldSx : [fieldSx])]}
					dataTestId={dataTestId && `${dataTestId}-picker`}
				/>
			</Box>
			{wasRounded && <FormHelperText>{t("hour.list.roundedToHour")}</FormHelperText>}
		</Box>
	);
};

export default HourListField;
